import { memo, useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, Badge, ModalLayout, Button, Skeleton, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import CapabilitiesCatalogBanner from '../provider/CapabilitiesCatalogBanner.jsx';
import { useInstanceCapabilities } from '../../hooks/instance';
import ModalFooter from '../../components/modals/parts/ModalFooter.jsx';
import { CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../utils';
import { AlertTriangle, Boxes, KeyRound, RefreshCw, ShieldAlert, Zap } from 'lucide-react';

const GROUPS = [
  {
    type: 'tool',
    icon: Zap,
    chip: 'bg-[rgba(167,139,250,0.10)] text-[#CDA5FF] border-[rgba(167,139,250,0.28)]',
    iconBox: 'bg-[rgba(167,139,250,0.10)] text-[#CDA5FF] border-[rgba(167,139,250,0.28)]',
  },
  {
    type: 'data_sync',
    icon: RefreshCw,
    chip: 'bg-[rgba(16,185,129,0.10)] text-[#34D39C] border-[rgba(16,185,129,0.28)]',
    iconBox: 'bg-[rgba(16,185,129,0.10)] text-[#34D39C] border-[rgba(16,185,129,0.28)]',
  },
  {
    type: 'mcp',
    icon: Boxes,
    chip: 'bg-[rgba(56,189,248,0.10)] text-[#7ED3F7] border-[rgba(56,189,248,0.28)]',
    iconBox: 'bg-[rgba(56,189,248,0.10)] text-[#7ED3F7] border-[rgba(56,189,248,0.28)]',
  },
];

function CapToggle({ on, label, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={[
        'relative h-5 w-[34px] shrink-0 rounded-full border transition-colors',
        on ? 'border-transparent bg-[#2B7FFF]' : 'border-edge bg-white/[0.06]',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white transition-[left]',
          on ? 'left-[18px]' : 'left-0.5',
        ].join(' ')}
      />
    </button>
  );
}

const CapabilityLibCard = memo(function CapabilityLibCard({ cap, icon: Icon, iconBox, onToggle, onViewExample, t }) {
  return (
    <div className="flex items-stretch overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <div className={`flex min-w-0 flex-1 items-start gap-2.5 px-3.5 py-3 ${cap.enabled ? '' : 'opacity-[0.62]'}`}>
        <span
          aria-hidden="true"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${iconBox}`}
        >
          <Icon size={14} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-snug tracking-[-0.005em] text-content">{cap.label}</p>
          <p className="mt-1 line-clamp-3 text-[11.5px] leading-normal text-content-secondary">{cap.description}</p>
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/[0.05] pt-2">
            <code className="min-w-0 truncate font-mono text-[10.5px] text-content-faint">{cap.id}</code>
            <button
              type="button"
              onClick={() => onViewExample(cap)}
              className="inline-flex shrink-0 items-center gap-0.5 text-[10.5px] font-semibold text-[#2B7FFF] hover:underline"
            >
              {t('integrations.instance.capabilities.viewExample')}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center border-l border-white/[0.05] px-3.5">
        <CapToggle
          on={cap.enabled}
          label={t(
            cap.enabled ? 'integrations.instance.capabilities.disable' : 'integrations.instance.capabilities.enable',
            { capability: cap.label },
          )}
          onClick={() => onToggle(cap, !cap.enabled)}
        />
      </div>
    </div>
  );
});

/**
 * Instance Detail — Capabilities tab (CT-4190). Mirrors prototype `instanceCapsHTML`:
 * editable banner, Tool / Data Sync / MCP widgets, 2-col capability cards with a
 * right-edge toggle. Ack modal still gates sensitive activations.
 */
const InstanceCapabilitiesTab = () => {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { instance } = useOutletContext();
  const { caps, grouped, isLoading, error, toggle } = useInstanceCapabilities(instance.instanceId);
  const [ackCap, setAckCap] = useState(null);

  // Stable identities so the memo on CapabilityLibCard holds: with the hook's
  // optimistic update keeping unchanged caps' refs, toggling one card must not
  // re-render the rest. Declared before the early returns (rules of hooks).
  const onToggle = useCallback(
    (cap, next) => {
      if (next && cap.requires_ack) {
        setAckCap(cap);
        return;
      }
      toggle(cap.id, next);
    },
    [toggle],
  );

  const notifyStudio = useCallback(
    () => toast.info({ message: t('integrations.provider.stubs.dataStudio'), canClose: true }),
    [toast, t],
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const totalActive = caps.filter((c) => c.enabled).length;
  const typeLabel = (type) => t(`integrations.card.capability.${type}`);

  const confirmAck = () => {
    if (ackCap) toggle(ackCap.id, true);
    setAckCap(null);
  };

  return (
    <div className="flex flex-col gap-3.5">
      <CapabilitiesCatalogBanner
        icon={KeyRound}
        title={t('integrations.instance.capabilities.bannerTitle', { name: instance.name })}
        badge={
          <Badge variant="neutral" size="small" className={`${CHIP_BASE} ${CHIP_TONE.accent} !uppercase`}>
            <span className={CHIP_LABEL}>{t('integrations.instance.capabilities.editable')}</span>
          </Badge>
        }
        subtitle={t('integrations.instance.capabilities.bannerBody', {
          count: totalActive,
          name: instance.name,
        })}
      />

      {GROUPS.map(({ type, icon: Icon, chip, iconBox }) => {
        const items = grouped[type] ?? [];
        const activeCount = items.filter((c) => c.enabled).length;
        return (
          <Card key={type} className="overflow-hidden">
            <Card.Body className="px-[18px] py-0">
              <div className="flex flex-wrap items-center gap-2 pb-2.5 pt-3.5">
                <h3 className="min-w-0 flex-1">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${chip}`}
                  >
                    <Icon size={11} strokeWidth={2} aria-hidden="true" />
                    {typeLabel(type)}
                  </span>
                </h3>
                <span className="text-[11px] text-content-secondary">
                  {t('integrations.instance.capabilities.activeOf', {
                    active: activeCount,
                    total: items.length,
                  })}
                </span>
                {type === 'data_sync' && items.length > 0 ? (
                  <button
                    type="button"
                    onClick={notifyStudio}
                    className="text-[11.5px] font-semibold text-[#2B7FFF] hover:underline"
                  >
                    {t('integrations.instance.capabilities.openMapping')}
                    <span aria-hidden="true"> →</span>
                  </button>
                ) : null}
              </div>

              <div className="border-t border-edge pb-3.5 pt-3">
                <p className="mb-3.5 text-xs leading-normal text-content-secondary">
                  {t(`integrations.instance.capabilities.groupDesc.${type}`)}
                </p>
                {items.length === 0 ? (
                  <div className="w-full rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-[18px] py-[18px] text-center text-xs leading-normal text-content-faint">
                    {t('integrations.instance.capabilities.empty', {
                      name: instance.providerName,
                      type: typeLabel(type),
                    })}
                  </div>
                ) : (
                  <div
                    className={
                      type === 'mcp'
                        ? 'grid w-full grid-cols-1 gap-2.5'
                        : 'grid grid-cols-1 gap-2.5 md:grid-cols-2'
                    }
                  >
                    {items.map((cap) => (
                      <CapabilityLibCard
                        key={cap.id}
                        cap={cap}
                        icon={Icon}
                        iconBox={iconBox}
                        onToggle={onToggle}
                        onViewExample={notifyStudio}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        );
      })}

      {ackCap && (
        <ModalLayout
          isOpen
          onClose={() => setAckCap(null)}
          title={t('integrations.instance.capabilities.ackTitle')}
          titlePrefix={<ShieldAlert size={20} className="text-amber-600 dark:text-amber-400" />}
        >
          <div className="px-5 py-4">
            <StatusMessage
              icon={ShieldAlert}
              title={t('integrations.instance.capabilities.ackBody', { capability: ackCap.label })}
            />
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setAckCap(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={confirmAck}>
              {t('integrations.instance.capabilities.ackConfirm')}
            </Button>
          </ModalFooter>
        </ModalLayout>
      )}
    </div>
  );
};

export default InstanceCapabilitiesTab;
